// NIGHT-DIRECTSTAFF-2026-10-22 §4: the onboarding status board -- every ACTIVE onboarding in one table: the packet (sent /
// viewed / signed / returned), the exams (none / sent / finished / passed / failed), the documents still missing, and the
// days since anything last moved. Rows stuck more than 3 days rise to the top with Remind.
//
// MACHINE-READABLE ON PURPOSE: the onboarding-chaser agent will read this board later. Every value is a fixed lower-case
// word (BOARD_VALUES); the words people read are derived from them and never stored. boardRows() is pure.
import { linkState, STALLED_DAYS, isTestCandidate } from './onboardingOverview';
import { DOC_LABELS, HEALTH_KINDS } from './onboarding';

export const BOARD_VALUES = {
  packet: ['sent', 'viewed', 'signed', 'returned'],
  exams: ['none', 'sent', 'finished', 'passed', 'failed'],
  remind: ['packet', 'exams', 'none'],
};
export const PACKET_WORDS = { sent: 'Sent', viewed: 'Viewed', signed: 'Signing', returned: 'Returned' };
export const EXAM_WORDS = { none: 'No exams', sent: 'Sent', finished: 'Finished', passed: 'Passed', failed: 'Failed' };
// What the packet asks every traveler to upload (onboarding.js's checklist email); the I-20 only applies to some, so it
// is never counted as missing.
export const REQUIRED_DOCS = ['license', 'cpr', 'immunization', 'gov_id_front', 'gov_id_back', 'resume', 'voided_check'];
export const FORMS_TOTAL = 6;
const DAY = 864e5;
const t = (iso) => { const x = Date.parse(iso || ''); return Number.isNaN(x) ? null : x; };
const latest = (...isos) => isos.map(t).filter((x) => x !== null).reduce((a, b) => Math.max(a, b), -Infinity);

export function packetStatus(r) {
  if (r.status === 'complete' || r.completed_at) return 'returned';
  if (Number(r.forms_signed) > 0 || r.i9_signed_at || r.background_signed_at) return 'signed';
  if (r.esign_consented_at) return 'viewed';
  return 'sent';
}

/** The candidate's exams: none sent; sent (some unfinished); finished (all done, a fail among them -> failed); passed. */
export function examStatus(links = [], attempts = []) {
  const sent = new Set(links.filter((l) => !l.revoked_at).flatMap((l) => l.exams || []));
  if (!sent.size) return 'none';
  const best = new Map();
  for (const a of attempts) {
    if (!a.submitted_at) continue;
    const cur = best.get(a.exam_id);
    if (!cur || (a.passed && !cur.passed)) best.set(a.exam_id, a);
  }
  if ([...sent].some((id) => !best.has(id))) return 'sent';
  return [...sent].every((id) => best.get(id).passed) ? 'passed' : 'failed';
}

/**
 * overviewRows: onboarding_overview() rows (one per link); candidates; exams: { links, attempts } for everyone (or null);
 * documents: onboarding_documents rows { candidate_id, kind, uploaded_at } (or null = not readable); now.
 * -> rows, stuck first (most idle first), then the rest by name. Only ACTIVE onboardings: open links, and returned packets
 * not yet entered in ADP. Test candidates are left out.
 */
// DAY-DIRECTSTAFF-2026-10-22: health = onboarding_health_required rows { candidate_id, tb_test, physical } (or null before the
// migration): a health upload is "missing" only when staff ticked it required for that candidate.
export function boardRows({ overviewRows = [], candidates = [], exams = null, documents = null, health = null, now = Date.now() } = {}) {
  const byId = new Map((candidates || []).map((c) => [c.id, c]));
  const newest = new Map();
  for (const r of overviewRows || []) {
    const cur = newest.get(r.candidate_id);
    if (!cur || (t(r.created_at) || 0) > (t(cur.created_at) || 0)) newest.set(r.candidate_id, r);
  }
  const out = [];
  for (const r of newest.values()) {
    const c = byId.get(r.candidate_id);
    if (isTestCandidate(c)) continue;
    const state = linkState(r, now);
    if (state === 'revoked' || r.entered_in_adp_at || (state === 'expired' && !r.completed_at)) continue;
    const links = exams ? (exams.links || []).filter((l) => l.candidate_id === r.candidate_id) : [];
    const attempts = exams ? (exams.attempts || []).filter((a) => a.candidate_id === r.candidate_id) : [];
    const docs = documents ? documents.filter((d) => d.candidate_id === r.candidate_id) : null;
    const have = new Set((docs || []).map((d) => d.kind));
    const packet = packetStatus(r);
    const exam = examStatus(links, attempts);
    const moved = latest(r.last_activity_at, r.created_at, r.completed_at, ...links.map((l) => l.created_at),
      ...attempts.map((a) => a.submitted_at || a.started_at), ...(docs || []).map((d) => d.uploaded_at));
    const idleDays = moved === -Infinity ? null : Math.floor((now - moved) / DAY);
    const ticked = (health || []).find((h) => h.candidate_id === r.candidate_id);
    const outstanding = docs === null ? null : [...REQUIRED_DOCS, ...HEALTH_KINDS.filter((k) => ticked && ticked[k] === true)].filter((k) => !have.has(k));
    const stuck = idleDays !== null && idleDays > STALLED_DAYS && (packet !== 'returned' || exam === 'sent');
    const remind = !stuck ? 'none' : packet !== 'returned' && state === 'open' ? 'packet' : exam === 'sent' ? 'exams' : 'none';
    out.push({
      candidate_id: r.candidate_id, session_id: r.session_id, name: (c && c.name) || `Candidate #${r.candidate_id}`, discipline: (c && c.specialty) || '',
      packet, forms_signed: Number(r.forms_signed) || 0, exams: exam, outstanding, idle_days: idleDays,
      last_movement_at: moved === -Infinity ? null : new Date(moved).toISOString(), stuck, remind, link_open: state === 'open',
    });
  }
  return out.sort((a, b) => Number(b.stuck) - Number(a.stuck) || (b.stuck ? (b.idle_days || 0) - (a.idle_days || 0) : 0) || String(a.name).localeCompare(String(b.name)));
}

export const outstandingText = (keys) => (keys === null ? 'Not readable' : keys.length ? keys.map((k) => DOC_LABELS[k] || k).join(', ') : 'None');

/** The packet reminder -- the new link (same session, progress kept), short, nothing about pay. */
export function packetReminder(name, link) {
  const f = String(name || '').trim().split(/\s+/)[0];
  const hi = /^[A-Za-z][A-Za-z'.-]*$/.test(f) ? `Hi ${f},` : 'Hi,';
  return `${hi} a quick reminder to finish your ProTherapy onboarding checklist: ${link}. Everything you've done so far is saved. Reply here with any questions.`;
}
